import { Box } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { Image3D } from '~/domain/patient/image3d';
import {
    primaryImageState,
    secondaryImageState,
} from '~/state/atoms/image/imageAtoms';
import {
    histogramHintEnabledState,
    scaleDisplayEnabledState,
} from '~/state/atoms/toolsAtoms';
import { OverlayRow } from '../../OverlayLabel';
import WindowLevelHint from '../../WindowLevelHint';
import ImageDetails from './ImageDetails';
import WindowingControls from './WindowingControls';

interface ImageWindowingRowProps {
    image: Image3D;
    color: string;
    accentColor: 'primary' | 'secondary';
    showControls: boolean;
    showHistogram: boolean;
}

const ImageWindowingRow = ({
    image,
    color,
    accentColor,
    showControls,
    showHistogram,
}: ImageWindowingRowProps) => {
    const metadata = image.seriesMetadata;
    // Identity (modality · series · date) shows whenever the image exists.
    // The window/level editing controls and the histogram hint are situational
    // aids gated to the main pane (showControls) and to images that actually
    // carry a window/level; secondary panes render identity only.
    const showWindowingAids = showControls && Boolean(image.windowLevel);

    return (
        <Box display="flex" gap={1} alignItems="flex-end">
            <OverlayRow
                sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    flexShrink: 0,
                }}
            >
                <ImageDetails metadata={metadata} color={color} />
                {showWindowingAids && (
                    <WindowingControls image={image} color={color} />
                )}
            </OverlayRow>
            {showWindowingAids && showHistogram && (
                <WindowLevelHint
                    image={image}
                    accentColor={accentColor}
                    testIdSuffix={accentColor}
                />
            )}
        </Box>
    );
};

interface WindowingOptionsProps {
    showControls?: boolean;
}

const WindowingOptions = ({ showControls = true }: WindowingOptionsProps) => {
    const primaryImage = useRecoilValue(primaryImageState);
    const secondaryImage = useRecoilValue(secondaryImageState);
    const histogramHintEnabled = useRecoilValue(histogramHintEnabledState);
    const scaleDisplayEnabled = useRecoilValue(scaleDisplayEnabledState);
    // Ruler takes priority — when the scale display is on, suppress the
    // histogram so they don't share the bottom band.
    const showHistogram = histogramHintEnabled && !scaleDisplayEnabled;

    return (
        <Box display="flex" flexDirection="column" gap={0.5} flex="1 1 0">
            {primaryImage && (
                <ImageWindowingRow
                    image={primaryImage}
                    color="primary.main"
                    accentColor="primary"
                    showControls={showControls}
                    showHistogram={showHistogram}
                />
            )}
            {secondaryImage && (
                <ImageWindowingRow
                    image={secondaryImage}
                    color="secondary.main"
                    accentColor="secondary"
                    showControls={showControls}
                    showHistogram={showHistogram}
                />
            )}
        </Box>
    );
};

export default WindowingOptions;
